import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { AccountAdminSiteMenuLink } from '../types/accountAdminNav.types'

interface AccountAdminWebDrawerSiteLinksRegistryValue {
  links: AccountAdminSiteMenuLink[] | null
  register: (next: AccountAdminSiteMenuLink[] | null) => void
}

const AccountAdminWebDrawerSiteLinksContext =
  createContext<AccountAdminWebDrawerSiteLinksRegistryValue | null>(null)

export function AccountAdminWebDrawerSiteLinksProvider({ children }: { children: ReactNode }) {
  const [links, setLinks] = useState<AccountAdminSiteMenuLink[] | null>(null)
  const register = useCallback((next: AccountAdminSiteMenuLink[] | null) => {
    setLinks(next)
  }, [])
  const value = useMemo(
    () => ({
      links,
      register,
    }),
    [links, register],
  )
  return (
    <AccountAdminWebDrawerSiteLinksContext.Provider value={value}>
      {children}
    </AccountAdminWebDrawerSiteLinksContext.Provider>
  )
}

/** Lets the marketing landing or site shell override drawer site links (e.g. in-page scroll). */
export function useRegisterAccountAdminWebDrawerSiteLinks(
  next: AccountAdminSiteMenuLink[] | null,
): void {
  const ctx = useContext(AccountAdminWebDrawerSiteLinksContext)
  useEffect(() => {
    if (!ctx) {
      return undefined
    }
    ctx.register(next)
    return () => {
      ctx.register(null)
    }
  }, [ctx, next])
}

export function useAccountAdminWebDrawerSiteLinksRegistry(): AccountAdminWebDrawerSiteLinksRegistryValue | null {
  return useContext(AccountAdminWebDrawerSiteLinksContext)
}
